import React, { useMemo, useState } from 'react';
import { IonIcon, IonSkeletonText, useIonRouter, useIonToast } from '@ionic/react';
import {
  calendarOutline,
  chevronForwardOutline,
  trashOutline,
} from 'ionicons/icons';
import {
  useAttendanceSummary,
  useGroupMeetings,
  useResolveMeeting,
  useDeleteMeeting,
} from '@/hooks/useAttendance';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import type { AttendanceSummaryEntry, GroupMeeting } from '@/types/attendance';

interface Props {
  groupId: string;
  meetingDay: number | null; // 0-6 or null
  attendanceMode: 'roster' | 'headcount';
  canRecord: boolean;
  canDelete: boolean;
}

/** Local-date ISO (YYYY-MM-DD); midday avoids DST flips around tz boundaries. */
function todayIso(): string {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

/**
 * A meeting is "phantom" when it was opened today by the CTA but the user
 * never saved anything (no attendance, no headcount, no visitors). Hide only
 * today's empty rows — past empty meetings stay visible as "Fără prezență
 * înregistrată" so users can finish filling them in, and headcount-mode
 * meetings with null headcount on past dates aren't accidentally hidden.
 */
function isPhantom(m: GroupMeeting | AttendanceSummaryEntry): boolean {
  if (m.status !== 'held') return false;
  if (m.meeting_date !== todayIso()) return false;
  const totalAttendance =
    (m.present_count ?? 0) + (m.absent_count ?? 0) + (m.excused_count ?? 0);
  if (totalAttendance > 0) return false;
  if (m.headcount && m.headcount > 0) return false;
  const visitorCount = (m as GroupMeeting).visitor_count ?? 0;
  if (visitorCount > 0) return false;
  return true;
}

const DAY_NAMES = ['Dum', 'Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm'];
const DAY_NAMES_FULL = ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'];
const MONTH_SHORT = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Noi', 'Dec'];

/** Compute the date for the CTA based on meeting_day + today.
 *  - If meeting_day matches today → today
 *  - Else use the most recent past occurrence of meeting_day (<= 6 days back)
 *  - Null meeting_day (ad-hoc) → today
 */
function computeCtaDate(meetingDay: number | null): { iso: string; label: string } {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  let target = new Date(today);

  if (meetingDay !== null) {
    const todayDow = today.getDay();
    let diff = todayDow - meetingDay;
    if (diff < 0) diff += 7;
    if (diff > 0) target.setDate(target.getDate() - diff);
  }

  const iso = target.toISOString().slice(0, 10);
  const now = new Date();
  now.setHours(12, 0, 0, 0);
  const daysAgo = Math.round((now.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

  let label: string;
  if (daysAgo === 0) label = 'azi';
  else if (daysAgo === 1) label = 'ieri';
  else label = `${DAY_NAMES_FULL[target.getDay()]} ${target.getDate()}`;

  return { iso, label };
}

function formatDayMonth(iso: string): { day: string; month: string; dow: string } {
  const d = new Date(iso + 'T12:00:00');
  return {
    day: String(d.getDate()),
    month: (MONTH_SHORT[d.getMonth()] ?? '').toUpperCase(),
    dow: DAY_NAMES[d.getDay()] ?? '',
  };
}

const GroupAttendanceTab: React.FC<Props> = ({
  groupId,
  meetingDay,
  attendanceMode,
  canRecord,
  canDelete,
}) => {
  const router = useIonRouter();
  const [presentToast] = useIonToast();
  const [meetingToDelete, setMeetingToDelete] = useState<GroupMeeting | null>(null);

  const { data: summary, isLoading: summaryLoading } = useAttendanceSummary(groupId, 8);
  const { data: meetingsPage, isLoading: meetingsLoading } = useGroupMeetings(groupId);
  const resolveMut = useResolveMeeting();
  const deleteMut = useDeleteMeeting();

  const cta = useMemo(() => computeCtaDate(meetingDay), [meetingDay]);

  const recentMeetings = useMemo(
    () => (meetingsPage?.data ?? []).filter((m) => !isPhantom(m)).slice(0, 12),
    [meetingsPage],
  );

  const summaryEntries = useMemo(
    () => (summary?.data ?? []).filter((m) => !isPhantom(m)),
    [summary],
  );

  const lastHeld = useMemo(() => {
    return summaryEntries.slice().reverse().find((m) => m.status === 'held');
  }, [summaryEntries]);

  const maxCount = useMemo(() => {
    const vals = summaryEntries.map((m) =>
      attendanceMode === 'headcount' ? (m.headcount ?? 0) : m.present_count,
    );
    return Math.max(1, ...vals);
  }, [summaryEntries, attendanceMode]);

  const handleCta = async () => {
    try {
      const meeting = await resolveMut.mutateAsync({ groupId, date: cta.iso });
      router.push(`/meetings/${meeting.id}`, 'forward');
    } catch {
      void presentToast({ message: 'Eroare la deschiderea întâlnirii.', duration: 2500, color: 'danger', position: 'bottom' });
    }
  };

  const handleDelete = async () => {
    if (!meetingToDelete) return;
    try {
      await deleteMut.mutateAsync(meetingToDelete.id);
      void presentToast({ message: 'Întâlnire ștearsă.', duration: 1800, color: 'success', position: 'bottom' });
    } catch {
      void presentToast({ message: 'Eroare la ștergere.', duration: 2500, color: 'danger', position: 'bottom' });
    } finally {
      setMeetingToDelete(null);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      {/* Hero card — layout adapts to data density */}
      {summaryLoading ? (
        <IonSkeletonText animated style={{ height: 112, borderRadius: 14, marginBottom: 20 }} />
      ) : summaryEntries.length > 0 ? (
        summaryEntries.length >= 3 ? (
          /* ≥3 meetings: dense layout with sparkline */
          <div className="att-tab-hero">
            <div className="att-tab-hero__last">
              <span className="att-tab-hero__last-label">Ultima întâlnire</span>
              {lastHeld ? (
                <>
                  <span className="att-tab-hero__last-date">
                    {formatDayMonth(lastHeld.meeting_date).day} {formatDayMonth(lastHeld.meeting_date).month}
                  </span>
                  <span className="att-tab-hero__last-sub">
                    {attendanceMode === 'headcount'
                      ? `${lastHeld.headcount ?? 0} prezenți`
                      : `${lastHeld.present_count}/${lastHeld.present_count + lastHeld.absent_count + lastHeld.excused_count} prezenți`}
                  </span>
                </>
              ) : (
                <span className="att-tab-hero__last-sub">N-a avut loc încă</span>
              )}
            </div>
            <div className="att-tab-hero__sparkline" aria-label="Istoric prezență">
              {summaryEntries.map((m: AttendanceSummaryEntry) => {
                const val = attendanceMode === 'headcount' ? (m.headcount ?? 0) : m.present_count;
                const h = Math.max(6, Math.round((val / maxCount) * 40));
                return (
                  <div
                    key={m.id}
                    className={`att-sparkline-bar att-sparkline-bar--${m.status}`}
                    style={{ height: `${m.status === 'held' ? h : 6}px` }}
                    title={`${m.meeting_date}: ${m.status}`}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          /* 1-2 meetings: focused hero with attendance ring */
          <div className="att-tab-hero-compact">
            {lastHeld ? (() => {
              const total = lastHeld.present_count + lastHeld.absent_count + lastHeld.excused_count;
              const pct = total > 0
                ? Math.round((lastHeld.present_count / total) * 100)
                : (lastHeld.headcount ? 100 : 0);
              const count = attendanceMode === 'headcount'
                ? (lastHeld.headcount ?? 0)
                : lastHeld.present_count;
              const dmy = formatDayMonth(lastHeld.meeting_date);
              return (
                <>
                  <div className="att-tab-hero-compact__ring" style={{ '--pct': pct } as React.CSSProperties}>
                    <div className="att-tab-hero-compact__ring-num">{pct}%</div>
                  </div>
                  <div className="att-tab-hero-compact__body">
                    <span className="att-tab-hero__last-label">Ultima întâlnire</span>
                    <span className="att-tab-hero-compact__date">
                      {dmy.dow}, {dmy.day} {dmy.month}
                    </span>
                    <span className="att-tab-hero-compact__sub">
                      {attendanceMode === 'headcount'
                        ? `${count} prezenți`
                        : `${count}/${total} prezenți`}
                    </span>
                  </div>
                </>
              );
            })() : (
              <div className="att-tab-hero-compact__empty">
                <span className="att-tab-hero__last-label">Ultima întâlnire</span>
                <span className="att-tab-hero-compact__sub" style={{ marginTop: 6 }}>
                  N-a avut loc încă
                </span>
              </div>
            )}
          </div>
        )
      ) : null}

      {/* Primary CTA */}
      {canRecord && (
        <button className="att-cta-main" onClick={handleCta} disabled={resolveMut.isPending}>
          <IonIcon icon={calendarOutline} style={{ fontSize: 18 }} />
          {resolveMut.isPending ? 'Se deschide…' : 'Înregistrează prezența'}
          <span className="att-cta-main__date">{cta.label}</span>
        </button>
      )}

      {/* Last meetings list */}
      <div className="att-roster-heading" style={{ marginTop: 8 }}>
        <h3>Întâlniri recente</h3>
      </div>

      {meetingsLoading ? (
        <div className="att-meeting-list">
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9' }}>
              <IonSkeletonText animated style={{ width: '60%', height: 16 }} />
            </div>
          ))}
        </div>
      ) : recentMeetings.length === 0 ? (
        <div className="att-empty">
          <div className="att-empty__icon">
            <IonIcon icon={calendarOutline} />
          </div>
          <h3 className="att-empty__title">Nicio întâlnire înregistrată</h3>
          <p className="att-empty__text">
            {canRecord
              ? 'Apasă butonul de mai sus pentru a înregistra prima întâlnire.'
              : 'Acest grup nu are încă întâlniri.'}
          </p>
        </div>
      ) : (
        <div className="att-meeting-list">
          {recentMeetings.map((m: GroupMeeting) => {
            const { day, month, dow } = formatDayMonth(m.meeting_date);
            const total = (m.present_count ?? 0) + (m.absent_count ?? 0) + (m.excused_count ?? 0);
            return (
              <div
                key={m.id}
                className="att-meeting-list__row"
                onClick={() => router.push(`/meetings/${m.id}`, 'forward')}
              >
                <div className="att-meeting-list__date-box">
                  <div className="att-meeting-list__date-num">{day}</div>
                  <div className="att-meeting-list__date-month">{month}</div>
                </div>
                <div className="att-meeting-list__body">
                  <div className="att-meeting-list__title">
                    {dow}, {day} {month}
                  </div>
                  <div className="att-meeting-list__sub">
                    {m.status === 'held' ? (
                      attendanceMode === 'headcount'
                        ? `${m.headcount ?? 0} prezenți`
                        : total > 0
                          ? `${m.present_count}/${total} prezenți${(m.excused_count ?? 0) > 0 ? ` · ${m.excused_count} motivați` : ''}`
                          : m.headcount !== null && m.headcount !== undefined
                            ? `${m.headcount} prezenți (arhivat)`
                            : 'Fără prezență înregistrată'
                    ) : m.status === 'cancelled' ? 'Anulată' : 'Sărită'}
                    {(m.visitor_count ?? 0) > 0 && ` · ${m.visitor_count} vizitatori`}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className={`att-chip att-chip--${m.status}`}>
                    {m.status === 'held' ? 'Ținută' : m.status === 'cancelled' ? 'Anulată' : 'Sărită'}
                  </span>
                  {canDelete && (
                    <button
                      type="button"
                      className="att-meeting-list__delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMeetingToDelete(m);
                      }}
                      aria-label="Șterge întâlnirea"
                      title="Șterge întâlnirea"
                    >
                      <IonIcon icon={trashOutline} />
                    </button>
                  )}
                  <IonIcon icon={chevronForwardOutline} style={{ fontSize: 16, color: '#cbd5e1' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!meetingToDelete}
        title="Șterge întâlnirea"
        message={
          meetingToDelete
            ? `Ștergi definitiv întâlnirea din ${meetingToDelete.meeting_date}? Toată prezența înregistrată va fi pierdută.`
            : ''
        }
        confirmLabel="Șterge"
        variant="danger"
        isLoading={deleteMut.isPending}
        onConfirm={handleDelete}
        onCancel={() => setMeetingToDelete(null)}
      />
    </div>
  );
};

export default GroupAttendanceTab;
